import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AccountPage } from "@common-components/account-page/account-page";
import BTThemeProvider from "@common-components/bt-theme-provider";
import ConfirmationDialog from "@common-components/confirmation-dialog/confirmation-dialog";
import { selectUserObject } from "@common-reducers/UserSelectors";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { setUser } from '@/common/reducers/UserReducer';
import { DBUserThunks } from '@common/reducers/DBServiceThunks';
import { namespace } from '@common/config/constants';
import { flowPath } from '@common/utils/clear-flow';
import { UserInterface } from "@common/models/user";
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Snackbar,
  Switch,
  TextField,
} from '@mui/material';
import { Stack } from '@mui/system';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Shared defaults + media resolver — single source with the engine resolvers,
// so the settings UI and the runtime scheduler can never drift on defaults.
import { BACKUP_DEFAULTS, resolveMedia } from '@extension/services/vault-backup/backup-config';
import { apiCall } from '@extension/wa-communication-layer/api/api-call-helper';
import classes from "../account-view/account-view.module.scss";

type BackupConfig = NonNullable<UserInterface['backup']>;
type MediaConfig = NonNullable<BackupConfig['media']>;
type ResolvedMedia = Required<MediaConfig>;
type GatewayConfig = NonNullable<BackupConfig['gateway']>;
interface ResolvedGateway {
  vaultEnabled: boolean;
  dbBackupEnabled: boolean;
  vaultRetentionDays: number;
  backupIntervalMinutes: number;
  mediaTtlDays: number;
  media: ResolvedMedia;
}
interface ResolvedBackupConfig {
  vaultEnabled: boolean;
  dbBackupEnabled: boolean;
  vaultRetentionDays: number;
  backupIntervalHours: number;
  mediaTtlDays: number;
  media: ResolvedMedia;
  ui: { showCountdown: boolean };
  testIntervalMinutes: number;
  gateway: ResolvedGateway;
}

const bool = (v: boolean | undefined, dflt: boolean) => (typeof v === 'boolean' ? v : dflt);
const num = (v: number | undefined, dflt: number) => (typeof v === 'number' && Number.isFinite(v) ? v : dflt);

/** Fully-defaulted EDITABLE view of the backup doc. Unlike the engine's
 *  `resolveBackupConfig` (which collapses to one active context), the UI edits
 *  the extension AND gateway sub-trees + test override simultaneously — so it
 *  keeps its own shape but shares the DEFAULT VALUES via BACKUP_DEFAULTS. */
const resolveBackupConfig = (backup?: BackupConfig): ResolvedBackupConfig => {
  const d = BACKUP_DEFAULTS;
  const g = backup?.gateway;
  return {
    vaultEnabled: bool(backup?.vaultEnabled, d.vaultEnabled),
    dbBackupEnabled: bool(backup?.dbBackupEnabled, d.dbBackupEnabled),
    vaultRetentionDays: num(backup?.vaultRetentionDays, d.vaultRetentionDays),
    backupIntervalHours: num(backup?.backupIntervalHours, d.backupIntervalHours),
    mediaTtlDays: num(backup?.mediaTtlDays, d.mediaTtlDays),
    media: resolveMedia(backup?.media),
    ui: { showCountdown: bool(backup?.ui?.showCountdown, d.showCountdown) },
    testIntervalMinutes: num(backup?.testIntervalMinutes, 0),
    gateway: {
      vaultEnabled: bool(g?.vaultEnabled, d.gateway.vaultEnabled),
      dbBackupEnabled: bool(g?.dbBackupEnabled, d.gateway.dbBackupEnabled),
      vaultRetentionDays: num(g?.vaultRetentionDays, d.gateway.vaultRetentionDays),
      backupIntervalMinutes: num(g?.backupIntervalMinutes, d.gateway.backupIntervalMinutes),
      mediaTtlDays: num(g?.mediaTtlDays, d.gateway.mediaTtlDays),
      media: resolveMedia(g?.media),
    },
  };
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const MEDIA_TYPES: Array<keyof Omit<ResolvedMedia, 'maxMb'>> = [
  'audio', 'images', 'documents', 'videos', 'archives',
];

export interface AccountBackupViewProps {
  /** Chromeless mode for the `/clear-account-backup` route — see AccountPage's `bare`. */
  bare?: boolean;
}

const AccountBackupView = ({ bare = false }: AccountBackupViewProps = {}) => {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const user: UserInterface | undefined = useAppSelector(selectUserObject);

  const cfg = useMemo(() => resolveBackupConfig(user?.backup), [user?.backup]);
  // Authoritative latest config for patching — updated both when the user doc
  // changes AND optimistically inside patchBackup before the round-trip. Using
  // the render-captured `cfg` there caused rapid toggles to clobber each other
  // (each patch built from the same stale snapshot).
  const latestRef = useRef(cfg);
  useEffect(() => { latestRef.current = cfg; }, [cfg]);

  // Local editable strings for the numeric fields so free typing isn't clamped mid-edit.
  // They commit (patch) on blur; toggles/checkboxes patch on change (save-on-change UX).
  const [retentionInput, setRetentionInput] = useState(String(cfg.vaultRetentionDays));
  const [intervalInput, setIntervalInput] = useState(String(cfg.backupIntervalHours));
  const [mediaTtlInput, setMediaTtlInput] = useState(String(cfg.mediaTtlDays));
  const [maxMbInput, setMaxMbInput] = useState(String(cfg.media.maxMb));

  const [confirmMode, setConfirmMode] = useState<null | 'enable' | 'disable' | 'vaultDisable'>(null);
  // Gateway config + test override are debug-only (localStorage['bt-debug']==='true').
  const btDebug = (() => {
    try { return localStorage.getItem('bt-debug') === 'true'; } catch { return false; }
  })();
  const [gwRetentionInput, setGwRetentionInput] = useState(String(cfg.gateway.vaultRetentionDays));
  const [gwIntervalInput, setGwIntervalInput] = useState(String(cfg.gateway.backupIntervalMinutes));
  const [gwMediaTtlInput, setGwMediaTtlInput] = useState(String(cfg.gateway.mediaTtlDays));
  const [gwMaxMbInput, setGwMaxMbInput] = useState(String(cfg.gateway.media.maxMb));
  const [testIntervalInput, setTestIntervalInput] = useState(String(cfg.testIntervalMinutes));
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackBarSeverity, setSnackBarSeverity] = useState<'success' | 'error'>('success');
  const [snackbarMessage, setSnackbarMessage] = useState<string>();
  const [extendingMedia, setExtendingMedia] = useState(false);
  const [triggeringBackup, setTriggeringBackup] = useState(false);

  useEffect(() => {
    document.title = t('accountBackupView.pageTitle');
  }, [t]);

  // Keep local numeric inputs in sync when the user document changes elsewhere.
  useEffect(() => { setRetentionInput(String(cfg.vaultRetentionDays)); }, [cfg.vaultRetentionDays]);
  useEffect(() => { setIntervalInput(String(cfg.backupIntervalHours)); }, [cfg.backupIntervalHours]);
  useEffect(() => { setMediaTtlInput(String(cfg.mediaTtlDays)); }, [cfg.mediaTtlDays]);
  useEffect(() => { setMaxMbInput(String(cfg.media.maxMb)); }, [cfg.media.maxMb]);
  useEffect(() => { setGwRetentionInput(String(cfg.gateway.vaultRetentionDays)); }, [cfg.gateway.vaultRetentionDays]);
  useEffect(() => { setGwIntervalInput(String(cfg.gateway.backupIntervalMinutes)); }, [cfg.gateway.backupIntervalMinutes]);
  useEffect(() => { setGwMediaTtlInput(String(cfg.gateway.mediaTtlDays)); }, [cfg.gateway.mediaTtlDays]);
  useEffect(() => { setGwMaxMbInput(String(cfg.gateway.media.maxMb)); }, [cfg.gateway.media.maxMb]);
  useEffect(() => { setTestIntervalInput(String(cfg.testIntervalMinutes)); }, [cfg.testIntervalMinutes]);

  const patchBackup = useCallback(async (changes: Partial<BackupConfig>) => {
    if (!user?._id) return;
    // Build from the REF (latest merged config), not the render-captured cfg,
    // so two quick toggles don't clobber each other. Send the fully-resolved
    // object so Mongoose (strict:true) keeps every subfield.
    const base = latestRef.current;
    const nextBackup: BackupConfig = {
      ...base,
      ...changes,
      media: { ...base.media, ...(changes.media ?? {}) },
      ui: { ...base.ui, ...(changes.ui ?? {}) },
      // deep-merge the gateway sub-object so a single gateway field change
      // doesn't drop the others (strict:true needs the full sub-doc).
      gateway: {
        ...base.gateway,
        ...(changes.gateway ?? {}),
        media: { ...base.gateway.media, ...(changes.gateway?.media ?? {}) },
      },
      // preserve backend-managed read-only field
      lastBackupAt: user?.backup?.lastBackupAt,
    };
    // Optimistically advance the ref so a concurrent patch merges onto this.
    latestRef.current = resolveBackupConfig(nextBackup);
    const res: any = await dispatch(DBUserThunks.patch({
      entity: { _id: user._id, backup: nextBackup },
    }));
    if (res?.payload) {
      dispatch(setUser(res.payload));
    }
    if (res?.error) {
      setSnackBarSeverity('error');
      setSnackbarMessage(t('accountBackupView.snackbarError'));
      setOpenSnackbar(true);
    } else {
      setSnackBarSeverity('success');
      setSnackbarMessage(t('accountBackupView.snackbarSaved'));
      setOpenSnackbar(true);
    }
  }, [dispatch, t, user?._id, user?.backup?.lastBackupAt]);

  const patchMedia = useCallback((key: keyof MediaConfig, value: boolean | number) => {
    patchBackup({ media: { [key]: value } as Partial<MediaConfig> });
  }, [patchBackup]);

  const patchGateway = useCallback((changes: Partial<GatewayConfig>) => {
    patchBackup({ gateway: changes });
  }, [patchBackup]);

  const patchGatewayMedia = useCallback((key: keyof MediaConfig, value: boolean | number) => {
    patchBackup({ gateway: { media: { [key]: value } as Partial<MediaConfig> } });
  }, [patchBackup]);

  // dbBackupEnabled is gated behind a confirmation dialog (opt-in server storage).
  const handleDbBackupToggle = (checked: boolean) => {
    setConfirmMode(checked ? 'enable' : 'disable');
  };

  // Turning the vault OFF also disables deleted-message recovery → confirm.
  const handleVaultToggle = (checked: boolean) => {
    if (checked) { patchBackup({ vaultEnabled: true }); return; }
    setConfirmMode('vaultDisable');
  };

  const handleConfirm = () => {
    const mode = confirmMode;
    setConfirmMode(null);
    if (mode === 'vaultDisable') { patchBackup({ vaultEnabled: false }); return; }
    patchBackup({ dbBackupEnabled: mode === 'enable' });
  };

  const commitNumber = (
    raw: string,
    min: number,
    max: number,
    current: number,
    apply: (value: number) => void,
    resetInput: (value: string) => void,
  ) => {
    const parsed = Number(raw);
    if (raw.trim() === '' || Number.isNaN(parsed)) {
      resetInput(String(current));
      return;
    }
    const value = clamp(Math.round(parsed), min, max);
    resetInput(String(value));
    if (value !== current) apply(value);
  };

  const lastBackupAt = user?.backup?.lastBackupAt;
  const lastBackupLabel = (() => {
    if (lastBackupAt === undefined || lastBackupAt === null || lastBackupAt === '') return '—';
    const date = new Date(lastBackupAt);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(i18n.language || undefined);
  })();

  const controlsDisabled = !cfg.vaultEnabled;

  const numberField = (
    label: string,
    helper: string,
    value: string,
    onInput: (v: string) => void,
    onCommit: () => void,
    min: number,
    max: number,
    disabled: boolean,
  ) => (
    <TextField
      type="number"
      size="small"
      label={label}
      helperText={helper}
      value={value}
      disabled={disabled}
      onChange={(e) => onInput(e.target.value)}
      onBlur={onCommit}
      inputProps={{ min, max, step: 1 }}
      sx={{
        maxWidth: 260,
        backgroundColor: 'var(--color-surface)',
        '& .MuiInputBase-input': { color: 'var(--color-text)' },
      }}
    />
  );

  const backupView = () => (
    <div className={classes.pageContainer}>
      <div className={classes.titleContainer}>
        <h4 className={classes.pageTitle}>{t('accountBackupView.pageTitle')}</h4>
      </div>
      <div className={classes.accountSettingBody}>
        <Stack spacing={4} sx={{ maxWidth: 640 }}>
          <p style={{ color: 'var(--color-text)', fontSize: 14, margin: 0, opacity: 0.85 }}>
            {t('accountBackupView.intro')}
          </p>

          {/* Master toggles */}
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={cfg.vaultEnabled}
                  onChange={(e) => handleVaultToggle(e.target.checked)}
                />
              }
              label={t('accountBackupView.vaultEnabled')}
              sx={{ color: 'var(--color-text)' }}
            />
            <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7, marginInlineStart: 48 }}>
              {t('accountBackupView.vaultEnabledHelp')}
            </span>
          </FormGroup>

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={cfg.dbBackupEnabled}
                  disabled={controlsDisabled}
                  onChange={(e) => handleDbBackupToggle(e.target.checked)}
                />
              }
              label={t('accountBackupView.dbBackupEnabled')}
              sx={{ color: 'var(--color-text)' }}
            />
            <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7, marginInlineStart: 48 }}>
              {t('accountBackupView.dbBackupEnabledHelp')}
            </span>
          </FormGroup>

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={cfg.ui.showCountdown}
                  disabled={controlsDisabled}
                  onChange={(e) => patchBackup({ ui: { showCountdown: e.target.checked } })}
                />
              }
              label={t('accountBackupView.showCountdown')}
              sx={{ color: 'var(--color-text)' }}
            />
            <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7, marginInlineStart: 48 }}>
              {t('accountBackupView.showCountdownHelp')}
            </span>
          </FormGroup>

          {/* Manual "Back up now" — stamps backup.forceRunAt; the engine scheduler
              (running on the WhatsApp tab) sees it on its next poll and runs a
              backup ONCE, bypassing the idle/interval gates. For verifying the
              backup end-to-end without waiting for the schedule. */}
          <Stack spacing={0.5} sx={{ alignSelf: 'flex-start' }}>
            <Button
              size="small"
              variant="contained"
              disabled={controlsDisabled || !cfg.dbBackupEnabled || triggeringBackup}
              onClick={async () => {
                setTriggeringBackup(true);
                try {
                  await patchBackup({ forceRunAt: Date.now() });
                  setSnackBarSeverity('success');
                  setSnackbarMessage('Backup triggered — it runs on the WhatsApp tab within ~1 minute.');
                } catch {
                  setSnackBarSeverity('error');
                  setSnackbarMessage('Failed to trigger backup.');
                } finally {
                  setOpenSnackbar(true);
                  setTriggeringBackup(false);
                }
              }}
              sx={{ textTransform: 'none' }}
            >
              {triggeringBackup ? 'Triggering…' : 'Back up now'}
            </Button>
            <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7 }}>
              Runs one backup on your WhatsApp tab within ~1 minute (needs the WhatsApp tab open + server backup on).
            </span>
          </Stack>

          <Divider sx={{ borderColor: 'var(--color-border)' }} />

          {/* Numbers */}
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>
            {t('accountBackupView.retentionSectionTitle')}
          </span>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {numberField(
              t('accountBackupView.vaultRetentionDays'),
              t('accountBackupView.vaultRetentionDaysHelp'),
              retentionInput,
              setRetentionInput,
              () => commitNumber(retentionInput, 2, 30, cfg.vaultRetentionDays,
                (v) => patchBackup({ vaultRetentionDays: v }), setRetentionInput),
              2, 30, controlsDisabled,
            )}
            {numberField(
              t('accountBackupView.backupIntervalHours'),
              t('accountBackupView.backupIntervalHoursHelp'),
              intervalInput,
              setIntervalInput,
              () => commitNumber(intervalInput, 1, 168, cfg.backupIntervalHours,
                (v) => patchBackup({ backupIntervalHours: v }), setIntervalInput),
              1, 168, controlsDisabled,
            )}
            {numberField(
              t('accountBackupView.mediaTtlDays'),
              t('accountBackupView.mediaTtlDaysHelp'),
              mediaTtlInput,
              setMediaTtlInput,
              () => commitNumber(mediaTtlInput, 1, 365, cfg.mediaTtlDays,
                (v) => patchBackup({ mediaTtlDays: v }), setMediaTtlInput),
              1, 365, controlsDisabled,
            )}
            {numberField(
              t('accountBackupView.mediaMaxMb'),
              t('accountBackupView.mediaMaxMbHelp'),
              maxMbInput,
              setMaxMbInput,
              () => commitNumber(maxMbInput, 1, 100, cfg.media.maxMb,
                (v) => patchMedia('maxMb', v), setMaxMbInput),
              1, 100, controlsDisabled,
            )}
          </Stack>

          {/* Apply the current media-retention to ALREADY-backed-up media too.
              Expiry is app-managed, so re-stamping pushes existing objects to the
              new date (e.g. 7-day media → a year). */}
          <Button
            size="small"
            variant="outlined"
            disabled={controlsDisabled || extendingMedia}
            onClick={async () => {
              setExtendingMedia(true);
              try {
                const res = await apiCall<{ extended?: number; expiresAt?: string; error?: string }>(
                  'post', '/actions?cmd=backup-extend-media-ttl', { days: cfg.mediaTtlDays },
                );
                if (res?.error) {
                  setSnackBarSeverity('error');
                  setSnackbarMessage(res.error);
                } else {
                  setSnackBarSeverity('success');
                  setSnackbarMessage(`Extended ${res?.extended ?? 0} media object(s) to ${cfg.mediaTtlDays} days.`);
                }
              } catch {
                setSnackBarSeverity('error');
                setSnackbarMessage('Failed to extend media TTL.');
              } finally {
                setOpenSnackbar(true);
                setExtendingMedia(false);
              }
            }}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            {extendingMedia ? 'Extending…' : `Extend existing media to ${cfg.mediaTtlDays} days`}
          </Button>

          <Divider sx={{ borderColor: 'var(--color-border)' }} />

          {/* Media-type checkboxes */}
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>
            {t('accountBackupView.mediaSectionTitle')}
          </span>
          <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7, marginTop: -8 }}>
            {t('accountBackupView.mediaSectionHelp')}
          </span>
          <FormGroup>
            {MEDIA_TYPES.map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={Boolean(cfg.media[type])}
                    disabled={controlsDisabled}
                    onChange={(e) => patchMedia(type, e.target.checked)}
                  />
                }
                label={t(`accountBackupView.media_${type}`)}
                sx={{ color: 'var(--color-text)' }}
              />
            ))}
          </FormGroup>

          {btDebug && (
            <>
              <Divider sx={{ borderColor: 'var(--color-border)' }} />
              <Alert severity="info" variant="outlined" sx={{ color: 'var(--color-text)' }}>
                {t('accountBackupView.debugSectionNote')}
              </Alert>

              {/* Test-interval override (any context) */}
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>
                {t('accountBackupView.testSectionTitle')}
              </span>
              {numberField(
                t('accountBackupView.testIntervalMinutes'),
                t('accountBackupView.testIntervalMinutesHelp'),
                testIntervalInput,
                setTestIntervalInput,
                () => commitNumber(testIntervalInput, 0, 1440, cfg.testIntervalMinutes,
                  (v) => patchBackup({ testIntervalMinutes: v }), setTestIntervalInput),
                0, 1440, false,
              )}

              {/* Gateway (dedicated-server) Retention & schedule */}
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>
                {t('accountBackupView.gatewaySectionTitle')}
              </span>
              <span style={{ fontSize: 12, color: 'var(--color-text)', opacity: 0.7, marginTop: -8 }}>
                {t('accountBackupView.gatewaySectionHelp')}
              </span>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={cfg.gateway.dbBackupEnabled}
                      onChange={(e) => patchGateway({ dbBackupEnabled: e.target.checked })}
                    />
                  }
                  label={t('accountBackupView.gatewayDbBackupEnabled')}
                  sx={{ color: 'var(--color-text)' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={cfg.gateway.vaultEnabled}
                      onChange={(e) => patchGateway({ vaultEnabled: e.target.checked })}
                    />
                  }
                  label={t('accountBackupView.gatewayVaultEnabled')}
                  sx={{ color: 'var(--color-text)' }}
                />
              </FormGroup>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {numberField(
                  t('accountBackupView.vaultRetentionDays'),
                  t('accountBackupView.vaultRetentionDaysHelp'),
                  gwRetentionInput, setGwRetentionInput,
                  () => commitNumber(gwRetentionInput, 2, 30, cfg.gateway.vaultRetentionDays,
                    (v) => patchGateway({ vaultRetentionDays: v }), setGwRetentionInput),
                  2, 30, false,
                )}
                {numberField(
                  t('accountBackupView.gatewayIntervalMinutes'),
                  t('accountBackupView.gatewayIntervalMinutesHelp'),
                  gwIntervalInput, setGwIntervalInput,
                  () => commitNumber(gwIntervalInput, 1, 1440, cfg.gateway.backupIntervalMinutes,
                    (v) => patchGateway({ backupIntervalMinutes: v }), setGwIntervalInput),
                  1, 1440, false,
                )}
                {numberField(
                  t('accountBackupView.mediaTtlDays'),
                  t('accountBackupView.mediaTtlDaysHelp'),
                  gwMediaTtlInput, setGwMediaTtlInput,
                  () => commitNumber(gwMediaTtlInput, 1, 365, cfg.gateway.mediaTtlDays,
                    (v) => patchGateway({ mediaTtlDays: v }), setGwMediaTtlInput),
                  1, 365, false,
                )}
                {numberField(
                  t('accountBackupView.mediaMaxMb'),
                  t('accountBackupView.mediaMaxMbHelp'),
                  gwMaxMbInput, setGwMaxMbInput,
                  () => commitNumber(gwMaxMbInput, 1, 100, cfg.gateway.media.maxMb,
                    (v) => patchGatewayMedia('maxMb', v), setGwMaxMbInput),
                  1, 100, false,
                )}
              </Stack>
              <FormGroup>
                {MEDIA_TYPES.map((type) => (
                  <FormControlLabel
                    key={`gw-${type}`}
                    control={
                      <Checkbox
                        checked={Boolean(cfg.gateway.media[type])}
                        onChange={(e) => patchGatewayMedia(type, e.target.checked)}
                      />
                    }
                    label={t(`accountBackupView.media_${type}`)}
                    sx={{ color: 'var(--color-text)' }}
                  />
                ))}
              </FormGroup>
            </>
          )}

          <Divider sx={{ borderColor: 'var(--color-border)' }} />

          {/* Status */}
          <div>
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text)' }}>
              {t('accountBackupView.lastBackup')}:{' '}
            </span>
            <span style={{ fontSize: 14, color: 'var(--color-text)' }}>{lastBackupLabel}</span>
          </div>
        </Stack>
      </div>

      <ConfirmationDialog
        open={confirmMode !== null}
        title={t(
          confirmMode === 'vaultDisable' ? 'accountBackupView.vaultDisableDialogTitle'
            : confirmMode === 'disable' ? 'accountBackupView.disableDialogTitle'
              : 'accountBackupView.enableDialogTitle')}
        content={t(
          confirmMode === 'vaultDisable' ? 'accountBackupView.vaultDisableDialogContent'
            : confirmMode === 'disable' ? 'accountBackupView.disableDialogContent'
              : 'accountBackupView.enableDialogContent')}
        onCancel={() => setConfirmMode(null)}
        onConfirm={handleConfirm}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackBarSeverity}
          variant="filled"
          sx={{ '& .MuiButtonBase-root': { color: 'var(--color-surface)' } }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );

  // Admin/support-only for now (not exposed to regular users in production).
  // Guard direct navigation too — the sidebar link is already role-gated.
  const isBackupAdmin = user?.role === 'support' || user?.role === 'admin';
  // flowPath keeps a clear-flow visitor on /clear-account instead of dropping
  // them into the full product's account page.
  if (!isBackupAdmin) return <Navigate to={flowPath("/account")} replace />;

  return (
    <BTThemeProvider>
      <AccountPage page={backupView()} bare={bare} />
    </BTThemeProvider>
  );
};

export default AccountBackupView;
